import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { Button, Container, ProgressBar, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import BusinessLogo from '../../../assets/business.png';
import NavigationFooter from '../../../components/NavigationFooter/NavigationFooter';
import {
  deleteBusinessBranch,
  getBusinessCategories,
  getStates,
  updateBusinessBranchOffices,
} from '../../../services/commerce.service';
import {
  BusinessCategory,
  City,
  CommerceBranchesState,
  NewBranchValues,
  State,
} from '../../../types/add_commerce.types';
import { OfficeBranch } from '../../../types/business/request';
import { newBranch } from '../../../utils/data';
import { doRefsSubmissions } from '../../../utils/formikHelpers';
import { sortArrayAlphabetically, triggerToasts } from '../../../utils/helpers';
import { pagesPaths } from '../../../utils/navigationUtils';
import { getProgressPercentage } from '../../../utils/progressHelper';
import NewBranch from './NewBranch';

function CommerceBranches() {
  const location = useLocation();
  const navigate = useNavigate();
  const formRefs = useRef<any>([]);
  const localState: CommerceBranchesState = location.state as CommerceBranchesState;

  const [branches, setBranches] = useState<Array<NewBranchValues>>(
    localState?.branches?.length ? localState.branches : [{ ...newBranch, localId: uuidv4() }],
  );
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submittedBranches, setSubmittedBranches] = useState<Array<NewBranchValues>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [states, setStates] = useState<Array<State>>([]);
  const [businessCategories, setBusinessCategories] = useState<Array<BusinessCategory>>([]);

  useEffect(() => {
    const fetchStates = async () => {
      const responseStates = await getStates();
      setStates(responseStates);
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchBusinessCategories = async () => {
      const responseBusinessCategories = await getBusinessCategories();
      if (!Array.isArray(responseBusinessCategories.data)) return;
      setBusinessCategories(sortArrayAlphabetically(responseBusinessCategories.data, 'name'));
    };
    fetchBusinessCategories();
  }, []);

  const submitBranch = (values: NewBranchValues, index: number, state: State, city: City) => {
    const updateArray = [...submittedBranches];

    if (index === 0) {
      const branchOfficeSubCategoryId: string = location.state.businessCategoryId.toString();

      updateArray[index] = {
        ...values,
        businessCategoryId: branchOfficeSubCategoryId,
        state,
        city,
      };
    } else {
      updateArray[index] = { ...values, state, city };
    }

    setSubmittedBranches(updateArray);
  };

  const postBranches = async () => {
    setLoading(true);
    const officesBranch: OfficeBranch[] = submittedBranches?.map((branch) => ({
      fantasyName: branch.fantasyName,
      city: branch?.city?.id ? branch?.city?.id?.toString() : branch?.city?.toString(),
      addressStreet: branch.addressStreet,
      addressNumber: branch.addressNumber?.toString()!,
      addressFlatNumber: branch.addressFlatNumber?.toString()! || '',
      addressSolar: branch.addressSolar?.toString()! || '',
      addressPlot: branch.addressPlot?.toString()! || '',
      businessSubCategory: branch.businessCategoryId,
    }));

    const response = await updateBusinessBranchOffices(localState.id!, { officesBranch });

    if (response.result && response.data) {
      const businessData = response.data.businessData || response.data;

      setLoading(false);

      return navigate(pagesPaths.CommerceBeneficiaries, { state: { ...localState, ...businessData } });
    }
    triggerToasts(response);
    setLoading(false);
  };

  useEffect(() => {
    if (submitted && submittedBranches.length === branches.length) {
      setSubmitted(false);
      postBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, submittedBranches, localState, navigate]);

  const handleGoBack = () => navigate(pagesPaths.CommerceAddress, { state: { ...localState } });

  const handleSubmit = () => {
    if (formRefs.current) {
      const didFormRefsSubmitted: boolean = doRefsSubmissions(formRefs);
      didFormRefsSubmitted && setSubmitted(true);
    }
  };

  const handleAddNewBranch = () => setBranches([...branches, { ...newBranch, localId: uuidv4() }]);

  const handleDeleteBranch = async (index: number) => {
    const branchToDelete = branches.find(
      (branch: NewBranchValues, filterIndex: number) => index === filterIndex,
    );
    if (branchToDelete?.id) {
      const response = await deleteBusinessBranch(localState.id!, branchToDelete?.id);

      if (response.result) {
        return setBranches(response.data.branches);
      }
      return triggerToasts(response);
    }

    const branchesFiltered = branches.filter(
      (branch: NewBranchValues, filterIndex: number) => index !== filterIndex,
    );

    setBranches(branchesFiltered);
  };

  return (
    <>
      <Container className="branches">
        <ProgressBar now={getProgressPercentage('CommerceBranches')} />

        <h1>
          <img src={BusinessLogo} alt="Comercio" className="me-3" /> Sucursales
        </h1>
        <br />
        <Row>
          {branches?.map((branch: NewBranchValues, index: number) => (
            <NewBranch
              innerRef={(formElement: any) => (formRefs.current[index] = formElement)}
              submitBranch={submitBranch}
              key={branch.localId || `${branch.fantasyName}_${index}`}
              index={index}
              branchData={branch}
              handleDelete={handleDeleteBranch}
              states={states}
              businessCategories={businessCategories}
            />
          ))}
        </Row>

        <Button onClick={handleAddNewBranch} className="add-section">
          <FontAwesomeIcon icon={faPlus as IconProp} />
          Agregar sucursal
        </Button>
      </Container>
      <NavigationFooter previousHandler={handleGoBack} nextHandler={handleSubmit} loading={loading} />
    </>
  );
}

export default CommerceBranches;
